import * as XLSX from 'xlsx';
import { saveReportFile, salvarRelatorioPdf } from '../utils/reportFiles';
import { cycleMetrics, hasValue, performanceChange } from './performanceModel';
import { carregarLogoRelatorio, criarCabecalhoRelatorio, criarDocumentoRelatorio, criarTabelaRelatorio } from '../utils/reportLayout';

export const formatPerformanceValue = (value, currency = false, percent = false) => value == null ? 'Dados insuficientes'
  : Number(value).toLocaleString('pt-BR', currency
    ? { style: 'currency', currency: 'BRL' }
    : { maximumFractionDigits: 2 }) + (percent ? '%' : '');

const notice = 'Consulta dos registros atuais; não é um fechamento imutável. Ausência de gastos não comprova custo zero. Projeções não são vendas nem lucro. Na agricultura, custo por hectare exige área e gastos em todos os ciclos selecionados; custo por unidade usa ciclos com resultado informado, exigindo área e gastos em todos eles, por cultura/unidade.';
const columns = ['Indicador', 'Selecionado', 'Referência', 'Diferença', 'Variação (%)'];

function comparisonRows(metrics) {
  return metrics.map(metric => {
    const change = performanceChange(metric.current, metric.previous);
    return [metric.label, metric.current == null ? 'Dados insuficientes' : metric.percent ? metric.current / 100 : metric.current,
      metric.previous == null ? 'Dados insuficientes' : metric.percent ? metric.previous / 100 : metric.previous,
      change ? change.difference : 'Dados insuficientes',
      !change ? 'Dados insuficientes' : change.percent == null ? 'Sem base percentual' : change.percent / 100];
  });
}

export function createComparisonWorkbook({ metrics, currentLabel, previousLabel, context }, generatedAt = new Date()) {
  const sheet = XLSX.utils.aoa_to_sheet([
    ['Relatório de Comparação de Desempenho'],
    ['Propriedade', context],
    ['Selecionado', currentLabel],
    ['Referência', previousLabel],
    ['Gerado em', generatedAt.toLocaleString('pt-BR')],
    [],
    columns,
    ...comparisonRows(metrics),
    [],
    [notice],
  ]);
  metrics.forEach((metric, index) => {
    [1, 2, 3, 4].forEach(column => {
      const cell = sheet[XLSX.utils.encode_cell({ r: index + 7, c: column })];
      if (cell?.t === 'n') cell.z = column === 4 || (metric.percent && column < 3) ? '0.00%'
        : metric.percent && column === 3 ? '0.00" p.p."' : metric.currency ? '"R$" #,##0.00;-"R$" #,##0.00' : '#,##0.00';
    });
  });
  sheet['!cols'] = [{ wch: 52 }, { wch: 35 }, { wch: 35 }, { wch: 24 }, { wch: 24 }];
  sheet['!autofilter'] = { ref: `A7:E${7 + metrics.length}` };
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'Comparacao');
  return workbook;
}

export function createComparisonPdf({ metrics, currentLabel, previousLabel, context, logo }, generatedAt = new Date()) {
  const rows = comparisonRows(metrics).map((row, index) => row.map((value, column) => (
    typeof value !== 'number' ? value : column === 4
      ? `${formatPerformanceValue(value * 100)}%` : metrics[index].percent
        ? column < 3 ? formatPerformanceValue(value * 100, false, true) : `${formatPerformanceValue(value)} p.p.`
        : formatPerformanceValue(value, metrics[index].currency)
  )));
  const table = criarTabelaRelatorio([columns, ...rows], true);
  table.table.widths = ['*', 110, 110, 110, 100];
  table.table.body.forEach(row => row.forEach((cell, index) => { cell.alignment = index ? 'right' : 'left'; }));
  return {
    ...criarDocumentoRelatorio([
      criarCabecalhoRelatorio('Relatório de Comparação de Desempenho', { propriedade: context, area: 'Histórico de desempenho' }, logo, generatedAt),
      { text: `Selecionado: ${currentLabel}`, margin: [0, 6, 0, 3] },
      { text: `Referência: ${previousLabel}`, margin: [0, 0, 0, 3] },
      table,
      { text: notice, fontSize: 8, color: '#666666', margin: [0, 8, 0, 0] },
    ], generatedAt),
    pageOrientation: 'landscape',
  };
}

export function createAgricultureWorkbook({ rows, estimates, costs, context, filterLabel, comparison }, generatedAt = new Date()) {
  const workbook = XLSX.utils.book_new();
  const sheet = XLSX.utils.aoa_to_sheet([
    ['Relatório de Desempenho dos Ciclos Agrícolas'],
    ['Propriedade', context],
    ['Filtros', filterLabel],
    ['Gerado em', generatedAt.toLocaleString('pt-BR')],
    [],
    ['Talhão', 'Cultura', 'Plantio', 'Colheita efetiva', 'Área (ha)', 'Unidade', 'Meta por ha', 'Real por ha', 'Custo registrado (R$)', 'Receita projetada (R$)', 'Saldo projetado (R$)'],
    ...rows.map(row => {
      const estimate = estimates[row._performanceKey] || {};
      const metrics = cycleMetrics(row, estimate, costs);
      return [row.nome, row.cultura, row.data_plantio, row.data_colheita_efetiva || 'Em aberto', metrics.area,
        estimate.unidade || 'Não informada', hasValue(estimate.produtividade) ? Number(estimate.produtividade) : 'Não informada',
        hasValue(estimate.real) ? Number(estimate.real) : 'Não informado',
        metrics.cost ?? 'Sem registros', metrics.revenue ?? 'Dados insuficientes', metrics.margin ?? 'Dados insuficientes'];
    }),
    [],
    [notice],
    ['Área, metas e cotações refletem o cadastro atual. Gastos gerais e sem ciclo compatível não estão incluídos.'],
  ]);
  rows.forEach((row, index) => {
    [4, 6, 7, 8, 9, 10].forEach(column => {
      const cell = sheet[XLSX.utils.encode_cell({ r: index + 6, c: column })];
      if (cell?.t === 'n') cell.z = column >= 8 ? '"R$" #,##0.00;-"R$" #,##0.00' : '#,##0.00';
    });
  });
  sheet['!cols'] = [30, 22, 16, 20, 14, 18, 18, 18, 24, 26, 26].map(wch => ({ wch }));
  sheet['!autofilter'] = { ref: `A6:K${6 + rows.length}` };
  XLSX.utils.book_append_sheet(workbook, sheet, 'Ciclos');
  if (comparison) XLSX.utils.book_append_sheet(workbook, createComparisonWorkbook({ ...comparison, context }, generatedAt).Sheets.Comparacao, 'Comparacao');
  return workbook;
}

export async function savePerformanceWorkbook(workbook, filename) {
  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  await saveReportFile(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), filename);
}

export async function exportPerformanceComparison(format, data) {
  const name = String(data.context || 'propriedade').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '_').toLowerCase();
  const filename = `comparacao_desempenho_${name}`;
  if (format === 'pdf') {
    const logo = await carregarLogoRelatorio();
    await salvarRelatorioPdf(createComparisonPdf({ ...data, logo }), `${filename}.pdf`);
  } else if (format === 'xlsx') {
    await savePerformanceWorkbook(createComparisonWorkbook(data), `${filename}.xlsx`);
  } else {
    throw new Error('Formato de relatório não suportado.');
  }
}
